/**
 * SupportContactMessage Component
 * 
 * Displays a consistent support contact message with automatic error context gathering.
 * Generates a mailto link with pre-populated error details for easy user reporting.
 */

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Typography, Link } from '@mui/material';
import { generateSupportMailto, SUPPORT_EMAIL, extractUserContext } from '../../Helpers/supportContact';
import { useAuth } from '../../Contexts/AuthContext';

/**
 * SupportContactMessage Component
 * 
 * @param {Object} props - Component props
 * @param {string} props.errorMessage - User-facing error message
 * @param {Object} props.errorDetails - Technical error details (stack, statusCode, etc.)
 * @param {string} props.subject - Email subject line
 * @param {Object} props.context - Additional context (projectId, etc.)
 * @param {string} props.variant - Display variant: 'inline' or 'block'
 * @param {boolean} props.showErrorDetails - Whether to show error message in component
 */
function SupportContactMessage({
  errorMessage = '',
  errorDetails = {},
  subject = 'Application Error',
  context = {},
  variant = 'inline',
  showErrorDetails = true
}) {
  const auth = useAuth();
  const activeUser = auth?.activeUser;
  
  // Memoize mailto generation to avoid recalculation on every render
  const mailtoLink = useMemo(() => {
    // Extract user context
    const userContext = extractUserContext(activeUser);
    
    // Merge with provided context
    const fullContext = {
      ...userContext,
      ...context
    };
    
    return generateSupportMailto({
      subject,
      errorMessage,
      errorDetails,
      context: fullContext
    });
  }, [errorMessage, errorDetails, subject, context, activeUser]);
  
  // Render based on variant
  if (variant === 'block') {
    return (
      <Typography variant="body2" component="div">
        {showErrorDetails && errorMessage && (
          <Typography variant="body2" sx={{ mb: 2 }}>
            <strong>ERROR:</strong> {errorMessage}
          </Typography>
        )}
        <Typography variant="body2">
          If the problem persists, please contact BridgeFirst support at{' '}
          <Link 
            href={mailtoLink} 
            target="_blank" 
            rel="noopener noreferrer"
            sx={{ fontWeight: 500 }}
          >
            {SUPPORT_EMAIL}
          </Link>
          .
        </Typography>
      </Typography>
    );
  }
  
  // Default: inline variant
  return (
    <Typography variant="body2">
      {showErrorDetails && errorMessage && (
        <>
          <strong>ERROR:</strong> {errorMessage}
          <br />
          <br />
        </>
      )}
      If the problem persists, please contact BridgeFirst support at{' '}
      <Link 
        href={mailtoLink} 
        target="_blank" 
        rel="noopener noreferrer"
        sx={{ fontWeight: 500 }}
      >
        {SUPPORT_EMAIL}
      </Link>
      .
    </Typography>
  );
}

SupportContactMessage.propTypes = {
  errorMessage: PropTypes.string,
  errorDetails: PropTypes.shape({
    stack: PropTypes.string,
    statusCode: PropTypes.number,
    endpoint: PropTypes.string,
    isTimeout: PropTypes.bool,
  }),
  subject: PropTypes.string,
  context: PropTypes.object,
  variant: PropTypes.oneOf(['inline', 'block']),
  showErrorDetails: PropTypes.bool,
};

export default SupportContactMessage;
